import React, { useMemo, useState } from "react";
import { useAuth } from "../../auth";
import { canAccessReportSource, canAccessReports } from "../permissions";
import { createReportItem, addReportItem } from "../reportStore";
import { getAllowedOrientations, getReportTypeLabel, normalizeReportOrientation } from "../reportTypes";
import { useReportWorkspace } from "../useReportWorkspace";
import "../styles.css";

function AddToReportModal({
  type,
  sourceModule,
  defaultTitle,
  defaultNote,
  defaultOrientation,
  onClose,
  onConfirm,
  error,
}) {
  const orientations = getAllowedOrientations(type);
  const [title, setTitle] = useState(defaultTitle || getReportTypeLabel(type));
  const [note, setNote] = useState(defaultNote || "");
  const [orientation, setOrientation] = useState(normalizeReportOrientation(type, defaultOrientation));

  return (
    <div className="reports-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <div className="reports-modal" role="dialog" aria-modal="true" aria-label="Uložit do reportu" onMouseDown={(event) => event.stopPropagation()}>
        <div className="reports-modal-header">
          <div>
            <div className="reports-eyebrow">Reporty</div>
            <h2>Uložit do reportu</h2>
          </div>
          <button type="button" className="reports-icon-button" onClick={onClose} aria-label="Zavřít">x</button>
        </div>
        <label className="reports-field">
          <span>Název položky</span>
          <input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={120} />
        </label>
        <label className="reports-field">
          <span>Popisek</span>
          <textarea value={note} onChange={(event) => setNote(event.target.value)} maxLength={600} rows={3} />
        </label>
        {orientations.length > 1 ? (
          <label className="reports-field">
            <span>Orientace</span>
            <select value={orientation} onChange={(event) => setOrientation(event.target.value)}>
              {orientations.map((value) => (
                <option key={value} value={value}>
                  {value === "landscape" ? "Na šířku" : "Na výšku"}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {error ? <div className="reports-inline-error">{error}</div> : null}
        <div className="reports-modal-actions">
          <button type="button" className="reports-secondary-button" onClick={onClose}>Zrušit</button>
          <button
            type="button"
            className="reports-primary-button"
            onClick={() =>
              onConfirm({
                title: title.trim() || defaultTitle || getReportTypeLabel(type),
                note: note.trim(),
                orientation,
                sourceModule,
              })
            }
          >
            Uložit
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AddToReportButton({
  type,
  sourceModule,
  buildSnapshot,
  defaultTitle,
  defaultNote = "",
  defaultOrientation,
  className = "",
}) {
  const auth = useAuth();
  const workspace = useReportWorkspace(auth.user?.id, auth.currentAreaId);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState("");
  const canUse = canAccessReports(auth) && canAccessReportSource(auth, sourceModule);
  const isAtLimit = workspace.items.length >= 30;
  const title = useMemo(() => String(defaultTitle || getReportTypeLabel(type)), [defaultTitle, type]);

  if (!canUse) return null;

  const onConfirm = ({ title: nextTitle, note, orientation }) => {
    setError("");
    if (isAtLimit) {
      setError("Report může obsahovat maximálně 30 položek.");
      return;
    }
    const snapshot = typeof buildSnapshot === "function" ? buildSnapshot() : null;
    if (!snapshot || typeof snapshot !== "object") {
      setError("Aktuální stav se nepodařilo zachytit.");
      return;
    }
    const item = createReportItem({
      type,
      sourceModule,
      areaId: auth.currentAreaId,
      areaName: auth.currentAreaName,
      title: nextTitle,
      note,
      orientation,
      snapshot,
    });
    const result = addReportItem(auth.user?.id, auth.currentAreaId, item);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setIsOpen(false);
  };

  return (
    <>
      <button
        type="button"
        className={`reports-save-button ${className}`}
        onClick={() => {
          setError("");
          setIsOpen(true);
        }}
        disabled={isAtLimit}
        title={isAtLimit ? "Report může obsahovat maximálně 30 položek." : "Uložit do reportu"}
      >
        Uložit do reportu
      </button>
      {isOpen ? (
        <AddToReportModal
          type={type}
          sourceModule={sourceModule}
          defaultTitle={title}
          defaultNote={defaultNote}
          defaultOrientation={defaultOrientation}
          onClose={() => setIsOpen(false)}
          onConfirm={onConfirm}
          error={error}
        />
      ) : null}
    </>
  );
}
